import React from 'react';
import { Card, HeaderBack } from './UI';

/**
 * Shared furniture for the admin user pages (profile and the create/edit
 * form), extracted so the two cannot drift apart. Anything visual that both
 * screens show — the blue panel headers, the label/value pairs, the tinted
 * action buttons, the back bar — lives here rather than being copied.
 */

// ─────────────────────────────────────────────
// Panels
// ─────────────────────────────────────────────

// Solid blue bar with white text — the same treatment the modal headers use,
// so a panel heading reads as the same kind of thing.
export const PanelTitle = ({ children, right }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 10, padding: '13px 18px', background: 'var(--accent)',
  }}>
    <span style={{
      fontSize: 11.5, fontWeight: 800, color: '#ffffff',
      textTransform: 'uppercase', letterSpacing: '0.07em',
    }}>{children}</span>
    {right}
  </div>
);

// The card is a flex column and the body claims the slack, so a panel given a
// height can hand that height on to its contents. For panels that size to
// their content this changes nothing — there is no slack to claim.
export const Panel = ({ title, right, children, pad = 18, style, bodyStyle }) => (
  <Card style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', ...style }}>
    <PanelTitle right={right}>{title}</PanelTitle>
    <div style={{ padding: pad, flex: 1, minHeight: 0, ...bodyStyle }}>{children}</div>
  </Card>
);

/** A label/value pair. Long values wrap rather than widening the grid. */
export const Field = ({ label, value, mono, full }) => (
  <div style={{ minWidth: 0, gridColumn: full ? '1 / -1' : undefined }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
    }}>{label}</div>
    <div style={{
      fontSize: 13.5, color: 'var(--text-primary)', fontWeight: 500,
      fontFamily: mono ? 'var(--mono)' : undefined,
      wordBreak: 'break-word', lineHeight: 1.5,
    }}>{value || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>—</span>}</div>
  </div>
);

/** Grid that reflows by available width rather than a fixed column count. */
export const FieldGrid = ({ children, min = 180 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 16 }}>
    {children}
  </div>
);

// ─────────────────────────────────────────────
// Action buttons
// ─────────────────────────────────────────────

/**
 * Tinted face for one action button.
 *
 * A faint vertical wash of the action's own colour over the card background,
 * a border and label in the same hue. Soft on purpose: several saturated
 * blocks stacked up would fight each other and make the destructive one no
 * louder than the rest, which is the whole reason for colouring them.
 *
 * Returned as an inline style because Btn merges its `style` prop after the
 * variant, so these values win outright over whichever variant is passed.
 */
export const actionFace = (token, { grow = true } = {}) => ({
  ...(grow ? { flex: 1, minHeight: 44 } : { minHeight: 42 }),
  width: '100%',
  justifyContent: 'center',
  background: `linear-gradient(180deg,`
    + ` color-mix(in srgb, var(--${token}) 15%, var(--bg-card)) 0%,`
    + ` color-mix(in srgb, var(--${token}) 5%, var(--bg-card)) 100%)`,
  border: `1px solid color-mix(in srgb, var(--${token}) 30%, transparent)`,
  color: `var(--${token})`,
  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.55),`
    + ` 0 1px 2px color-mix(in srgb, var(--${token}) 12%, transparent)`,
});

// ─────────────────────────────────────────────
// Back bar
// ─────────────────────────────────────────────

/**
 * The bar the Admin tabs use, so stepping into a user page looks like the
 * same kind of move as stepping into any other admin section. A plain
 * <button> rather than <Btn> because that is what AdminPage uses and the pill
 * styling is not one of the Btn variants.
 */
export const BackBar = ({ label, onBack, children }) => (
  <>
    {/* The Back itself lives in the top bar now; only the context stays here. */}
    <HeaderBack onClick={onBack} label={label} />
    {children && (
      <div
        className="flex-mobile-col"
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
          background: '#e4ebefdf', border: '1px solid black', borderRadius: 14,
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{children}</span>
      </div>
    )}
  </>
);

/** Shared responsive rule for the 300px rail + fluid content layout. */
export const ProfileGridStyles = () => (
  <style>{`
    @media (max-width: 900px) {
      .user-profile-grid { grid-template-columns: minmax(0, 1fr) !important; }
    }
  `}</style>
);

export const PROFILE_GRID = {
  display: 'grid',
  // Deliberately no alignItems:'start' — the columns stretch to the row height
  // so a rail panel can fill the space beside the long right column instead of
  // leaving a gap under it.
  gridTemplateColumns: 'minmax(0, 300px) minmax(0, 1fr)',
  gap: 16,
};
